import gsap from "gsap";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Germany map with free position input and heatmap display.
 * The Bundesland is derived from the clicked path for the Auswertung.
 */
export default class GermanyMap {
  constructor(element) {
    if (!element) throw new Error("Element is required");
    this.element = element;
    this.input = element.querySelector(".map__input");
    this.positionInput = element.querySelector(".map__position");
    this.svg = element.querySelector(".map__svg");

    if (!this.input || !this.positionInput || !this.svg) {
      throw new Error("Required elements are missing in map");
    }

    this.paths = Array.from(this.svg.querySelectorAll("path[data-land]"));
    this.radios = this.paths.filter(
      (path) => path.getAttribute("role") === "radio"
    );
    this.done = false;

    this.init();
  }

  init() {
    this.svg.querySelectorAll("[data-land]").forEach((target) => {
      target.addEventListener("click", (event) =>
        this.handleClick(target, event)
      );
    });

    this.radios.forEach((path) => {
      // Chromium wrongly matches :focus-visible on clicked SVG elements
      path.addEventListener("pointerdown", (event) => event.preventDefault());
      path.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        this.handleClick(path);
      });
    });

    this.input.addEventListener("showAverage", (event) =>
      this.showAverage(event)
    );

    this.initGhost();

    if (this.input.value) {
      this.select(this.input.value, this.storedPoint());
    }
  }

  initGhost() {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      return;
    }

    this.ghost = document.createElementNS(SVG_NS, "circle");
    this.ghost.classList.add("map__ghost");
    this.ghost.setAttribute("r", "9");
    this.svg.appendChild(this.ghost);

    this.svg.addEventListener("pointermove", (event) => {
      if (this.done || !event.target.closest("[data-land]")) {
        this.ghost.style.visibility = "hidden";
        return;
      }
      const point = this.svgPointFromEvent(event);
      this.ghost.setAttribute("cx", point.x);
      this.ghost.setAttribute("cy", point.y);
      this.ghost.style.visibility = "visible";
    });

    this.svg.addEventListener("pointerleave", () => {
      this.ghost.style.visibility = "hidden";
    });
  }

  storedPoint() {
    if (!this.positionInput.value) return this.centerOfLand(this.input.value);
    const [x, y] = this.positionInput.value.split(",").map(Number);
    if (Number.isNaN(x) || Number.isNaN(y)) return null;
    return { x, y };
  }

  svgPointFromEvent(event) {
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(
      this.svg.getScreenCTM().inverse()
    );
    return { x: Math.round(point.x), y: Math.round(point.y) };
  }

  pathCenter(path) {
    const box = path.getBBox();
    return {
      x: Math.round(box.x + box.width / 2),
      y: Math.round(box.y + box.height / 2),
    };
  }

  centerOfLand(land) {
    const path = this.radios.find((radio) => radio.dataset.land === land);
    return path ? this.pathCenter(path) : null;
  }

  handleClick(path, event) {
    if (this.done) return;
    const point =
      event && event.clientX !== undefined
        ? this.svgPointFromEvent(event)
        : this.pathCenter(path);
    this.select(path.dataset.land, point);
  }

  select(land, point) {
    this.input.value = land;
    if (point) {
      this.positionInput.value = `${point.x},${point.y}`;
      this.placeMarker(point);
    }
    this.radios.forEach((path) => {
      path.setAttribute("aria-checked", path.dataset.land === land);
    });
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  placeMarker(point) {
    if (!this.marker) {
      this.marker = document.createElementNS(SVG_NS, "circle");
      this.marker.classList.add("map__marker");
      this.marker.setAttribute("r", "9");
      this.svg.appendChild(this.marker);
    }
    this.marker.setAttribute("cx", point.x);
    this.marker.setAttribute("cy", point.y);

    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.fromTo(
        this.marker,
        { scale: 0.5, transformOrigin: "center" },
        { scale: 1, duration: 0.25, ease: "back.out(3)" }
      );
    }
  }

  showAverage(event) {
    this.done = true;
    this.element.classList.add("map--done");
    if (this.ghost) this.ghost.style.visibility = "hidden";

    this.radios.forEach((path) => {
      path.setAttribute("tabindex", "-1");
      path.setAttribute("aria-disabled", "true");
    });


    const positions = Array.isArray(event.detail.positions)
      ? event.detail.positions
      : [];

    this.renderHeat(positions);
  }

  renderHeat(positions) {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // White blobs accumulate alpha as a density field, the filter blurs it
    // and maps density to a yellow-to-red ramp via lookup tables
    const defs = document.createElementNS(SVG_NS, "defs");
    defs.innerHTML = `
      <filter id="map-heat-filter" filterUnits="userSpaceOnUse" x="-60" y="-60" width="712" height="921" color-interpolation-filters="sRGB">
        <feGaussianBlur stdDeviation="16" />
        <feColorMatrix type="matrix" values="0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 1 0" />
        <feComponentTransfer>
          <feFuncR type="table" tableValues="1 1 1 1 1 0.98 0.97 0.75 0.54" />
          <feFuncG type="table" tableValues="0.88 0.85 0.79 0.72 0.68 0.42 0.01 0 0" />
          <feFuncB type="table" tableValues="0.4 0.3 0.2 0.12 0.02 0.06 0.16 0.13 0.08" />
          <feFuncA type="table" tableValues="0 0.2 0.45 0.65 0.8 0.9 0.95 0.98 1" />
        </feComponentTransfer>
      </filter>
      <mask id="map-heat-mask" maskUnits="userSpaceOnUse" x="-60" y="-60" width="712" height="921">
        ${this.paths.map((path) => `<path d="${path.getAttribute("d")}" style="fill: #fff; stroke: #fff; stroke-width: 4px; transition: none" />`).join("")}
      </mask>
    `;
    this.svg.appendChild(defs);

    const group = document.createElementNS(SVG_NS, "g");
    group.classList.add("map__heat");
    group.setAttribute("filter", "url(#map-heat-filter)");
    group.setAttribute("mask", "url(#map-heat-mask)");

    positions.forEach((position) => {
      const [x, y] = position.split(",").map(Number);
      if (Number.isNaN(x) || Number.isNaN(y)) return;
      const circle = document.createElementNS(SVG_NS, "circle");
      circle.setAttribute("cx", x);
      circle.setAttribute("cy", y);
      circle.setAttribute("r", "36");
      circle.setAttribute("fill", "#fff");
      circle.setAttribute("fill-opacity", "0.22");
      group.appendChild(circle);
    });

    this.svg.appendChild(group);
    if (this.marker) this.svg.appendChild(this.marker);

    gsap.from(group, {
      opacity: 0,
      duration: reducedMotion ? 0 : 1.2,
      ease: "power1.inOut",
      delay: reducedMotion ? 0 : 0.15,
    });
  }
}
